import { Map, Record } from 'immutable';

import { StakeType } from 'app/lib/staking/constants';
import type { InitAction } from 'app/redux/actions/init';

import {
  CLEANUP, Cleanup,
  REFRESH_AVAILABILITY, RefreshAvailability,
} from '../../actions';
import {
  COMPLETE_REPLACE, CompleteReplace,
  DELETE_FROM_BET_SLIP, DeleteFromBetSlip,
  INSERT_IN_BET_SLIP, InsertInBetSlip,
} from '../../actions/entries';
import { CHANGE_STAKE_TYPE_AMOUNT, ChangeStakeTypeAmount } from '../../actions/stakeTypes';
import { CHANGE_SYSTEM_RANK, ChangeSystemRank } from '../../actions/stakeTypes/system';
import {
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_REQUEST, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeRequest,
  SubmitStakeSuccess,
} from '../../actions/submission';
import { AmountInputModifier, RankValue, SubmissionState } from '../../constants';
import { RecordOfSystemStakeTypeState, SystemStakeTypeState } from '../../types/stakeTypes/system';

// ---

export const initialState = Record<SystemStakeTypeState>({
  submission: SubmissionState.AWAITING,
  rank: null,
  rankOf: null,
  rankEntries: Map(),
  amountModifier: AmountInputModifier.DIVIDE,
}, 'systemStakeType');

export type SystemStakeTypeActions =
  | InitAction
  | InsertInBetSlip
  | DeleteFromBetSlip
  | CompleteReplace
  | ChangeSystemRank
  | ChangeStakeTypeAmount
  | RefreshAvailability
  | SubmitStakeRequest
  | SubmitStakeSuccess
  | SubmitStakeFailure
  | Cleanup;

// --

function setupRank(
  state: RecordOfSystemStakeTypeState,
  systemRankOf: number | null,
): RecordOfSystemStakeTypeState {
  if (!systemRankOf) {
    return state;
  }

  if (systemRankOf !== state.get('rankOf')) {
    state = state.merge({
      rank: RankValue.DEFAULT,
      rankOf: systemRankOf,
    });
  }

  return state;
}

// --

export default function (
  state = initialState(),
  action: SystemStakeTypeActions,
): RecordOfSystemStakeTypeState {
  switch (action.type) {
    case INSERT_IN_BET_SLIP:
    case DELETE_FROM_BET_SLIP:
    case COMPLETE_REPLACE: {
      const { systems } = action.payload;

      if (!systems) {
        return state;
      }

      state = state.merge({
        submission: SubmissionState.AWAITING,
        rankEntries: systems.rankEntries,
      });

      return setupRank(state, systems.systemRankOf);
    }

    case REFRESH_AVAILABILITY: {
      const { systems } = action.payload;

      if (!systems) {
        return state;
      }

      state = state.update('rankEntries', () => systems.rankEntries);

      return setupRank(state, systems.systemRankOf);
    }

    case CHANGE_SYSTEM_RANK:
      return state.set('rank', action.payload.value);

    case CHANGE_STAKE_TYPE_AMOUNT: {
      const { modifier, stakeType } = action.payload;

      if (modifier !== null && stakeType.has(StakeType.SYSTEM)) {
        state = state.set('amountModifier', modifier);
      }

      return state;
    }

    case SUBMIT_STAKE_REQUEST:
      return state.set('submission', SubmissionState.PROCESSING);

    case SUBMIT_STAKE_SUCCESS: {
      if (action.payload.isBigStake) {
        return state;
      }

      return state.set('submission', SubmissionState.ACCEPTED);
    }

    case SUBMIT_STAKE_FAILURE: {
      if (action.payload.isBigStake) {
        return state;
      }
      return state.set('submission', SubmissionState.FAILED);
    }

    case CLEANUP:
      return initialState();

    default:
      return state;
  }
}
